import axios from '@/lib/API/client'
import Memory from '@/lib/memory'
import { hasValue } from '@/lib/helpers/numeric'
import { belongsToSignedInUser, onePassAtATime, signedInUserId, toIsoDate } from '@/lib/helpers/send-queue'

// P7: the MOVE PRO launches made on this phone (sensor, horse, rider,
// discipline, start, stop), kept until the server has them. Each one is sent
// once its sensor records, again at the stop, and again while the network is
// missing: the server matches it with the sensor's file, whichever arrives
// first and however the file came (phone, Sync Box, web). Each one is sent only
// under the account that made it (send-queue.js).
//
// The former local matching kept its launches under this same key: the first
// start of this version sends them once.
const STORAGE_KEY = 'movepro-recording-intents'
const KEEP_MS = 90 * 24 * 60 * 60 * 1000
const MAX_RECORDING_INTENTS = 120

const toNullable = (value) => (hasValue(value) ? value : null)

const buildIntentId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

// What POST /recordings gets: the same uuid at the start and at the stop.
export const recordingIntentPayload = (intent) => ({
    uuid: String(intent.id),
    serial_id: String(intent.sensorSerialId),
    horse_id: toNullable(intent.horseId),
    rider_id: toNullable(intent.riderId),
    discipline: toNullable(intent.discipline),
    goal: toNullable(intent.goal),
    goal_value: toNullable(intent.goalValue),
    started_at: toIsoDate(intent.startedAt),
    ended_at: toIsoDate(intent.endedAt),
})

// A launch leaves once the server has its stop, or after 90 days.
const isKept = (intent, now = Date.now()) =>
    !!intent?.id &&
    !!intent?.sensorSerialId &&
    now - Number(intent.startedAt) <= KEEP_MS &&
    !(intent.sent && hasValue(intent.endedAt))

const readIntents = async () => {
    const stored = await Memory.get(STORAGE_KEY, { otherwise: [] })
    return (Array.isArray(stored) ? stored : []).filter((intent) => isKept(intent))
}

const writeIntents = (intents) =>
    Memory.set(
        STORAGE_KEY,
        intents
            .filter((intent) => isKept(intent))
            .sort((a, b) => b.startedAt - a.startedAt)
            .slice(0, MAX_RECORDING_INTENTS)
    )

// One change at a time: a stop never overwrites a start written meanwhile.
let changes = Promise.resolve()
const change = (mutation) => {
    const next = changes.then(async () => writeIntents(mutation(await readIntents())))
    changes = next.catch(() => undefined)
    return next
}

const sendPass = async () => {
    const toSend = (await readIntents()).filter((stored) => !stored.sent && belongsToSignedInUser(stored.userId))
    for (const intent of toSend) {
        try {
            await axios.post('/recordings', recordingIntentPayload(intent))
        } catch (error) {
            // No answer: the network is missing, the next pass sends it.
            if (!error?.response) return
            // Any other refusal but these two may pass later (logged out, server down).
            if (![403, 422].includes(error.response.status)) continue
            // 403: a horse its user no longer sees; 422: the uuid is another user's.
            console.warn('[Launch] Refused by the server, not sent again', intent.id, error.response.status)
        }

        // Sent as it was read: a stop written meanwhile still goes out.
        await change((intents) =>
            intents.map((stored) =>
                stored.id === intent.id && stored.endedAt === intent.endedAt ? { ...stored, sent: true } : stored
            )
        )
    }
}

export const sendRecordingIntents = onePassAtATime(sendPass)

export const createRecordingIntent = async ({
    sensorSerialId,
    horseId = null,
    riderId = null,
    discipline = null,
    goal = null,
    goalValue = null,
    startedAt = Date.now(),
} = {}) => {
    if (!hasValue(sensorSerialId)) {
        throw new Error('sensorSerialId is required to create a recording intent')
    }

    const intent = {
        id: buildIntentId(),
        userId: signedInUserId(),
        sensorSerialId: String(sensorSerialId),
        horseId: toNullable(horseId),
        riderId: toNullable(riderId),
        discipline: toNullable(discipline),
        goal: toNullable(goal),
        goalValue: toNullable(goalValue),
        startedAt: Number(startedAt),
        endedAt: null,
        sent: false,
    }

    // Not sent here: the record page sends it once the sensor records, and
    // removes it when the sensor fails to start.
    await change((intents) => [intent, ...intents])
    return intent
}

export const stopRecordingIntent = async (intentId, endedAt = Date.now()) => {
    if (!intentId) return

    await change((intents) =>
        intents.map((intent) =>
            intent.id === intentId ? { ...intent, endedAt: Number(endedAt), sent: false } : intent
        )
    )
    void sendRecordingIntents()
}

// A launch whose sensor never started: not sent again.
export const removeRecordingIntent = async (intentId) => {
    if (!intentId) return
    await change((intents) => intents.filter((intent) => intent.id !== intentId))
}
